import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeftIcon, CheckCircleIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';
const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const {
    resetPassword,
    isLoading
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Extract email and OTP from state passed during navigation
  const email = location.state?.email;
  const otp = location.state?.otp;
  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState(0);
  const strengthLabels = ['Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['bg-red-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
  const checkPasswordStrength = (password: string) => {
    let strength = 0;
    // Length check
    if (password.length >= 8) strength += 1;
    // Contains number check
    if (/\d/.test(password)) strength += 1;
    // Contains special character check
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 1;
    // Contains uppercase and lowercase
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 1;
    return Math.min(3, strength);
  };
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !otp) {
      setError('Missing verification information. Please start the password reset process again.');
      return;
    }
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    try {
      const success = await resetPassword(email, otp, password);
      if (success) {
        setIsSuccess(true);
        // After 3 seconds, redirect to login
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    }
  };
  if (isSuccess) {
    return <AuthLayout title="Success" subtitle="Password has been reset" data-id="element-708">
        <div className="mt-8 space-y-6 text-center" data-id="element-709">
          <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500" data-id="element-710" />
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-711">
            Password Reset Successful!
          </h2>
          <p className="text-sm text-gray-600" data-id="element-712">
            Your password has been successfully reset. Redirecting you to the
            login page...
          </p>
          <div className="mt-6" data-id="element-713">
            <Link to="/login" className="w-full inline-flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500" data-id="element-714">
              Go to Login
            </Link>
          </div>
        </div>
      </AuthLayout>;
  }
  return <AuthLayout title="New Password" subtitle="Create a secure password" data-id="element-715">
      <div className="mt-8 space-y-6" data-id="element-716">
        <div data-id="element-717">
          <Link to="/forgot-password" className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors mb-6" data-id="element-718">
            <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-719" />
            Back
          </Link>
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-720">
            Create a new password
          </h2>
          <p className="mt-2 text-sm text-gray-600" data-id="element-721">
            Your new password must be different from previously used passwords
          </p>
        </div>
        {error && <div className="p-4 rounded-md bg-red-50 text-red-700" data-id="element-722">{error}</div>}
        <form className="space-y-6" onSubmit={handleSubmit} data-id="element-723">
          <div className="space-y-4" data-id="element-724">
            <div data-id="element-725">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-726">
                New password
              </label>
              <div className="relative" data-id="element-727">
                <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required className="appearance-none block w-full pr-10 px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 transition-all" placeholder="New password" value={password} onChange={handlePasswordChange} minLength={8} data-id="element-728" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" onClick={() => setShowPassword(!showPassword)} data-id="element-729">
                  {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-730" /> : <EyeIcon className="h-5 w-5" data-id="element-731" />}
                </button>
              </div>
              {/* Password strength indicator */}
              {password.length > 0 && <div className="mt-2" data-id="element-732">
                  <div className="flex items-center justify-between mb-1" data-id="element-733">
                    <span className="text-xs font-medium text-gray-500" data-id="element-734">
                      Password strength:
                    </span>
                    <span className={`text-xs font-medium ${passwordStrength === 0 ? 'text-red-500' : passwordStrength === 1 ? 'text-yellow-500' : passwordStrength === 2 ? 'text-blue-500' : 'text-green-500'}`} data-id="element-735">
                      {strengthLabels[passwordStrength]}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-736">
                    <div className={`h-full ${strengthColors[passwordStrength]} transition-all duration-300 ease-in-out`} style={{
                  width: `${(passwordStrength + 1) * 25}%`
                }} data-id="element-737"></div>
                  </div>
                </div>}
              <ul className="mt-2 text-xs text-gray-500 space-y-1" data-id="element-738">
                <li className={`flex items-center ${password.length >= 8 ? 'text-green-500' : ''}`} data-id="element-739">
                  <span className="mr-1" data-id="element-740">•</span> At least 8 characters
                </li>
                <li className={`flex items-center ${/[A-Z]/.test(password) && /[a-z]/.test(password) ? 'text-green-500' : ''}`} data-id="element-741">
                  <span className="mr-1" data-id="element-742">•</span> Mix of uppercase & lowercase
                  letters
                </li>
                <li className={`flex items-center ${/\d/.test(password) ? 'text-green-500' : ''}`} data-id="element-743">
                  <span className="mr-1" data-id="element-744">•</span> At least one number
                </li>
                <li className={`flex items-center ${/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'text-green-500' : ''}`} data-id="element-745">
                  <span className="mr-1" data-id="element-746">•</span> At least one special character
                </li>
              </ul>
            </div>
            <div data-id="element-747">
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-748">
                Confirm new password
              </label>
              <div className="relative" data-id="element-749">
                <input id="confirm-password" name="confirm-password" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" required className={`appearance-none block w-full pr-10 px-4 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 transition-all ${confirmPassword && password !== confirmPassword ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`} placeholder="Confirm new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} data-id="element-750" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" onClick={() => setShowConfirmPassword(!showConfirmPassword)} data-id="element-751">
                  {showConfirmPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-752" /> : <EyeIcon className="h-5 w-5" data-id="element-753" />}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && <p className="mt-1 text-sm text-red-600" data-id="element-754">
                  Passwords do not match
                </p>}
            </div>
          </div>
          <div data-id="element-755">
            <button type="submit" disabled={isLoading || password !== confirmPassword} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-756">
              {isLoading ? <span className="flex items-center" data-id="element-757">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-758">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-759"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-760"></path>
                  </svg>
                  Resetting...
                </span> : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </AuthLayout>;
};
export default ResetPassword;